import React, { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Shield, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LoginPage() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [platformLogo, setPlatformLogo] = useState(null);
  const [logoLoaded, setLogoLoaded] = useState(false);

  useEffect(() => {
    setPlatformLogo(`${API_URL}/api/platform/logo?t=${Date.now()}`);
  }, []);

  const getHomeRoute = (role) => role === "family_user" ? "/family" : role === "super_admin" ? "/admin" : "/dashboard";
  if (user) return <Navigate to={getHomeRoute(user.role)} replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(email, password);
      toast.success("Bienvenido a Gerion");
      navigate(getHomeRoute(result?.user?.role));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al iniciar sesión");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center p-4" data-testid="login-page">
      <div className="w-full max-w-sm animate-fade-in-up">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
          <div className="flex flex-col items-center mb-8">
            <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-4 overflow-hidden" data-testid="login-logo">
              <img
                src={platformLogo || "/gerion-icon.png"}
                alt="Gerion"
                className="w-full h-full object-contain"
                onError={(e) => { e.target.onerror = null; e.target.src = "/gerion-icon.png"; }}
              />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight font-[Manrope]">Gerion</h1>
            <p className="text-sm text-slate-400 mt-1">Cuidar mejor, gestionar simple</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700 font-medium text-sm">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="correo@ejemplo.com"
                className="rounded-xl border-slate-200 h-11 text-slate-900 bg-slate-50 focus:bg-white focus:border-violet-500 focus:ring-2 focus:ring-violet-500/10"
                required
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700 font-medium text-sm">Contraseña</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPass ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="--------"
                  className="rounded-xl border-slate-200 h-11 text-slate-900 pr-11 bg-slate-50 focus:bg-white focus:border-violet-500 focus:ring-2 focus:ring-violet-500/10"
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  data-testid="toggle-password-btn"
                >
                  {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-xl h-11 bg-violet-600 text-white font-semibold text-sm hover:bg-violet-700 active:scale-[0.98] transition-all duration-200"
              data-testid="login-submit-btn"
            >
              {loading ? "Ingresando..." : "Iniciar Sesión"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
